import { motion } from "framer-motion";
import { itemImage, money } from "@/lib/api";
import { colorHex } from "@/lib/grouping";
import { Layers, ChevronUp } from "lucide-react";

/** Row of colour dots for a stack - one per piece, in colour order. */
export const ColorDots = ({ items, max = 6, size = "h-3 w-3" }) => (
  <span className="inline-flex items-center -space-x-1" data-testid="stack-color-dots">
    {items.slice(0, max).map((it) => (
      <span key={it.id} className={`${size} rounded-full border border-background ring-1 ring-black/10`} style={{ backgroundColor: colorHex(it.primary_color) }} title={it.primary_color || "colour unknown"} />
    ))}
    {items.length > max && <span className="ml-1.5 text-[10px] text-muted-foreground">+{items.length - max}</span>}
  </span>
);

/**
 * Task 4 - one card for several similar pieces (same brand + category + similar name).
 * Shows the first piece with two offset layers behind, a count and colour dots. Tap expands in place.
 */
export const StackCard = ({ stack, onExpand }) => {
  const top = stack.items[0];
  const worth = stack.items.reduce((s, i) => s + ((i.worth_override ?? i.estimated_worth) || 0), 0);
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="group relative text-left rounded-2xl bg-card lift" data-testid={`stack-card-${top.id}`}>
      <button onClick={onExpand} className="block w-full text-left press focus-visible:ring-0" aria-label={`Expand ${stack.items.length} ${stack.name}`} data-testid="stack-card-expand">
        <div className="relative pt-2 px-2">
          {/* offset layers behind the hero shot */}
          <span className="absolute inset-x-5 top-0 h-full rounded-2xl bg-gray-100 border border-border" aria-hidden />
          <span className="absolute inset-x-3.5 top-1 h-full rounded-2xl bg-gray-50 border border-border" aria-hidden />
          <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-white border border-border">
            <img src={itemImage(top)} alt={top.name || "garment"} loading="lazy" className="h-full w-full object-contain transition-transform duration-500 ease-spring group-hover:scale-[1.04]" />
            <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-foreground text-background px-2 h-6 text-[10px] font-semibold" data-testid="stack-card-count"><Layers className="h-3 w-3" /> {stack.items.length} pieces</span>
            <span className="absolute bottom-2 left-2 rounded-full bg-background/90 backdrop-blur px-2 h-6 inline-flex items-center"><ColorDots items={stack.items} /></span>
          </div>
        </div>
        <div className="px-1 pt-2.5 pb-1.5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold leading-tight" data-testid="stack-card-name">{stack.name}</p>
              <p className="truncate text-[11px] text-muted-foreground mt-0.5 capitalize">{[stack.brand, `${stack.items.length} colours`].filter(Boolean).join(" · ")}</p>
            </div>
            {worth > 0 && <p className="shrink-0 text-[13px] font-mono-num font-semibold">{money(worth)}</p>}
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5 truncate">{stack.items.map((i) => i.primary_color).filter(Boolean).join(" · ")}</p>
        </div>
      </button>
    </motion.div>
  );
};

/** Header row shown above an expanded stack inside the grid. */
export const StackHeader = ({ stack, onCollapse }) => (
  <div className="col-span-full flex items-center justify-between gap-3 rounded-xl bg-secondary/70 px-3 py-2 mt-1" data-testid={`stack-expanded-${stack.items[0].id}`}>
    <div className="flex items-center gap-2 min-w-0">
      <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
      <p className="text-sm font-medium truncate">{stack.brand ? `${stack.brand} · ` : ""}{stack.name} <span className="text-muted-foreground font-normal">· {stack.items.length} pieces</span></p>
      <ColorDots items={stack.items} />
    </div>
    <button onClick={onCollapse} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs text-muted-foreground hover:text-foreground hover:bg-background transition-colors shrink-0" data-testid="stack-collapse"><ChevronUp className="h-3.5 w-3.5" /> Stack</button>
  </div>
);
